import { useApiMutation, useApiQuery } from "@/hooks/use-api";
import {
	Alert,
	AlertCreate,
	AlertHistory,
	AlertRunResult,
	AlertTestResult,
	AlertUpdate,
} from "@/models/alert";
import { hasSelectedWorkspace } from "./core";
import { createExtendedResourceApi } from "./factory";

// Create API hooks using the factory
const alertApi = createExtendedResourceApi<Alert, AlertCreate, Partial<AlertCreate>>({
	resourcePath: "alerts",
	requireWorkspace: true,
	customEndpoints: {
		useToggle: {
			url: "alerts/{id}/toggle",
			method: "PATCH",
		},
		useTest: {
			url: "alerts/{id}/test",
			method: "POST",
		},
		useRun: {
			url: "alerts/{id}/run",
			method: "POST",
		},
		useHistory: {
			url: (id) => `alerts/${id}/history`,
			method: "GET",
			params: { limit: 50 },
		},
		useUpdateGroup: {
			url: "alerts/{id}",
			method: "PATCH",
		},
	},
});

/**
 * Récupère toutes les alertes
 */
export const useAlerts = alertApi.useList;

/**
 * Récupère une alerte par son ID
 */
export const useAlert = (id: string | undefined) => alertApi.useOne(id);

/**
 * Crée une nouvelle alerte
 */
export const useCreateAlert = alertApi.useCreate;

/**
 * Met à jour une alerte existante
 */
export const useUpdateAlert = alertApi.useUpdate;

/**
 * Supprime une alerte
 */
export const useDeleteAlert = alertApi.useDelete;

/**
 * Active/désactive une alerte
 */
export const useToggleAlert = () => 
	alertApi.useToggle() as ReturnType<typeof useApiMutation<Alert, { enabled: boolean }, { id: string }>>;

/**
 * Récupère l'historique d'une alerte
 */
export const useAlertHistory = (alertId: string | undefined, limit = 50) => {
	// For custom limit, we need to use the original API
	if (limit !== 50) {
		return useApiQuery<AlertHistory[]>({
			url: `alerts/${alertId}/history?limit=${limit}`,
			enabled: !!alertId && hasSelectedWorkspace(),
		});
	}
	return alertApi.useHistory(alertId) as ReturnType<typeof useApiQuery<AlertHistory[]>>;
};

/**
 * Teste une alerte sans déclencher de notifications
 */
export const useTestAlert = () => 
	alertApi.useTest() as ReturnType<typeof useApiMutation<AlertTestResult, Record<string, never>, { id: string }>>;

/**
 * Exécute une alerte manuellement
 */
export const useRunAlert = () => 
	alertApi.useRun() as ReturnType<typeof useApiMutation<AlertRunResult, Record<string, never>, { id: string }>>;

/**
 * Met à jour le groupe d'une alerte
 */
export const useUpdateAlertGroup = () => 
	alertApi.useUpdateGroup() as ReturnType<typeof useApiMutation<Alert, { group: { id: string } | null }, { id: string }>>;
